import { FC, Fragment, MouseEvent } from "react";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Skeleton, Tooltip } from "@mui/material";
import { useRouter } from "next/router";

import { useOperatorBusinessCaseStatusCounts } from "@/hooks/useOperatorBusinessCaseStatusCounts";

import {
  SectionInfoIconWrap,
  StepCardButton,
  StepCardCount,
  StepCardLabel,
  TimelineCard,
  TimelineHeader,
  TimelineSection,
  TimelineSectionCards,
  TimelineSectionChevron,
  TimelineSectionHeader,
  TimelineSectionTitle,
  TimelineSectionsTrack,
  TimelineTitle,
} from "./styles";
import {
  TIMELINE_SECTIONS,
  TimelineCardDef,
  getCardTotal,
} from "./timelineSteps";

export const StatusTimeline: FC = () => {
  const router = useRouter();
  const { data, actionCounts, isLoading } =
    useOperatorBusinessCaseStatusCounts();

  const handleCardClick = (card: TimelineCardDef, sectionLabel: string) => {
    // Encode the filter in the URL the trips page reads (via queryToFilters), so
    // it survives StrictMode double-mounts, refreshes and back-navigation. Keys
    // mirror filtersToQuery: `status` (comma-joined ids) or `funnelPhase` +
    // `funnelPhaseLabel`. hideTerminal defaults to true on the trips page, so we
    // omit it. The label is composed of section + card so the operator can tell
    // e.g. "Pending · Customer Signing" apart from another similarly-named card.
    const query: Record<string, string> = {};
    if (card.clickFilter.funnelPhase) {
      query.funnelPhase = card.clickFilter.funnelPhase;
      query.funnelPhaseLabel = `${sectionLabel} · ${card.label}`;
    } else if (card.clickFilter.statuses?.length) {
      query.status = card.clickFilter.statuses.join(",");
    }
    router.push({ pathname: "/trips", query });
  };

  return (
    <TimelineCard>
      <TimelineHeader>
        <TimelineTitle>Pipeline</TimelineTitle>
      </TimelineHeader>
      <TimelineSectionsTrack>
        {TIMELINE_SECTIONS.map((section, index) => {
          const isLast = index === TIMELINE_SECTIONS.length - 1;

          const stopCardNavigation = (
            event: MouseEvent<HTMLSpanElement>,
          ) => {
            event.stopPropagation();
          };

          return (
            <Fragment key={section.key}>
              <TimelineSection cardCount={section.cards.length}>
                <TimelineSectionHeader colorTheme={section.colorTheme}>
                  <TimelineSectionTitle>{section.label}</TimelineSectionTitle>
                  <Tooltip
                    title={section.description}
                    arrow
                    placement="top"
                    enterTouchDelay={0}
                  >
                    <SectionInfoIconWrap
                      onClick={stopCardNavigation}
                      aria-label={`About ${section.label}`}
                    >
                      <InfoOutlinedIcon fontSize="inherit" />
                    </SectionInfoIconWrap>
                  </Tooltip>
                </TimelineSectionHeader>

                <TimelineSectionCards>
                  {section.cards.map((card) => {
                    const totalCount = getCardTotal(
                      card,
                      data?.counts,
                      actionCounts,
                    );
                    return (
                      <StepCardButton
                        key={card.key}
                        type="button"
                        onClick={() =>
                          handleCardClick(card, section.label)
                        }
                        aria-label={`${card.label}: ${totalCount} business cases`}
                      >
                        <StepCardLabel>{card.label}</StepCardLabel>
                        {isLoading ? (
                          <Skeleton variant="text" width={40} height={32} />
                        ) : (
                          <StepCardCount>{totalCount}</StepCardCount>
                        )}
                      </StepCardButton>
                    );
                  })}
                </TimelineSectionCards>
              </TimelineSection>
              {!isLast && (
                <TimelineSectionChevron aria-hidden>
                  <ChevronRightIcon fontSize="inherit" />
                </TimelineSectionChevron>
              )}
            </Fragment>
          );
        })}
      </TimelineSectionsTrack>
    </TimelineCard>
  );
};
